import { Fragment, useState, useEffect, useRef, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './index.less';
import RichEditor from '@components/common/RichEditor';
import { useSurveyAutosave } from '../../SurveyAutosaveContext';
import { applyQuestionStemFocusBorder, clearQuestionStemFocusBorder } from '../richEditorFocusHelpers';
import { useQuestionStemAutosave } from '../useQuestionStemAutosave';
import { saveQuestionStemBody } from '../saveQuestionStemBody';

const Morecloze = ({ questionId, item, setAnserArea: _setAnserArea, anserArea, start, end }, ref) => {
    const { notifySaved } = useSurveyAutosave();
    const [questionIndex, setQuestionIndex] = useState();
    const [bodyHtml, setBodyHtml] = useState(item.body);
    const editorRootRef = useRef(null);
    const [seleteStart, setSeleteStart] = useState(0);
    const [seleteEnd, setSeleteEnd] = useState(0);
    const location = useLocation();
    const { pathname } = location;
    const anserAreaRef = useRef(anserArea);

    useEffect(() => {
        anserAreaRef.current = anserArea;
    }, [anserArea]);

    useImperativeHandle(ref, () => ({
        seleteStart,
        seleteEnd,
    }));

    const extraInit = useMemo(
        () => ({
            setup: editor => {
                editor.ui.registry.addButton('insertblank', {
                    text: '插入填空符',
                    onAction: () => {
                        editor.insertContent('______');
                    },
                });
            },
        }),
        [],
    );

    useEffect(() => {
        setBodyHtml(item.body);
    }, [item.body]);

    useEffect(() => {
        const index = anserArea.findIndex(it => it.id === item.id);
        setQuestionIndex(index + 1);
    }, [anserArea, item.id]);

    const onPersist = useCallback(
        async nextBody => {
            const ok = await saveQuestionStemBody({
                item,
                nextBody,
                anserArea: anserAreaRef.current,
                locationState: location.state,
                pathname,
            });
            if (ok) {
                notifySaved();
            }
            return ok;
        },
        [item, location.state, pathname, notifySaved],
    );

    const { schedule, flush } = useQuestionStemAutosave({
        itemId: item.id,
        remoteBody: item.body,
        onPersist,
    });

    const handleStemChange = useCallback(
        html => {
            setBodyHtml(html);
            schedule(html);
        },
        [schedule],
    );

    const changeTitle = (_editor, latestHtml) => {
        const nextBody = latestHtml != null ? latestHtml : bodyHtml;
        const root = editorRootRef.current;
        if (root) {
            clearQuestionStemFocusBorder(root);
        }
        setBodyHtml(nextBody);
        flush(nextBody);
    };

    const getBorder = () => {
        const root = editorRootRef.current;
        if (root) {
            applyQuestionStemFocusBorder(root);
        }
    };

    useEffect(() => {
        setSeleteStart(start);
        setSeleteEnd(end);
    }, [start, end]);

    return (
        <Fragment>
            <div name={questionId} onMouseDown={e => e.stopPropagation()}>
                <div className="question-title">
                    <div className="question-id">
                        <span
                            className="required"
                            style={{
                                display: item.required === 1 ? '' : 'none',
                            }}
                        >
                            *
                        </span>
                        <b className="question-index">{questionIndex}.</b>
                    </div>

                    <div ref={editorRootRef}>
                        <RichEditor
                            id={`wj-morecloze-question-${item.id}`}
                            mode="simple"
                            inline
                            value={bodyHtml}
                            onChange={handleStemChange}
                            placeholder="请输入问题"
                            className="question-title-area"
                            height={128}
                            toolbar="bold underline forecolor insertblank"
                            plugins="autolink advlist lists"
                            extraInit={extraInit}
                            onEditorFocus={getBorder}
                            onEditorBlur={changeTitle}
                        />
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default forwardRef(Morecloze);
